// src/components/mdx/client/visualizations/marketingDataVizData.ts

/**
 * Deterministic sample datasets for the marketing data visualization blog charts.
 *
 * @module marketingDataVizData
 */

import { createDeterministicRandom, hashSeed } from '~/lib/utils/random'
import { MARKETING_DATA_VIZ_PHASES } from '~/components/mdx/client/visualizations/blogChartPalette'

/**
 * Metric question types that map to chart families in marketing analytics.
 *
 * @typedef {('time-series' | 'comparison' | 'funnel' | 'retention' | 'scatter' | 'cohort')} MetricQuestionType
 */
export type MetricQuestionType =
  | 'time-series'
  | 'comparison'
  | 'funnel'
  | 'retention'
  | 'scatter'
  | 'cohort'

/**
 * Metadata describing a metric question and its recommended chart.
 *
 * @typedef {Object} MetricQuestionDefinition
 * @property {MetricQuestionType} id - Question identifier.
 * @property {string} question - Human-readable analytics question.
 * @property {string} chartType - Recommended visualization family.
 * @property {string} example - Marketing use-case example.
 */
export interface MetricQuestionDefinition {
  id: MetricQuestionType
  question: string
  chartType: string
  example: string
}

/**
 * Catalog of metric questions used by the chart selection explorer.
 */
export const METRIC_QUESTIONS: readonly MetricQuestionDefinition[] = [
  {
    id: 'time-series',
    question: 'How did efficiency trend over time?',
    chartType: 'Line chart',
    example: 'Weekly MER, blended CAC, platform ROAS'
  },
  {
    id: 'comparison',
    question: 'Which channel or creative wins?',
    chartType: 'Horizontal bar chart',
    example: 'CPA by channel, thumbstop by variant'
  },
  {
    id: 'funnel',
    question: 'Where does the funnel leak?',
    chartType: 'Funnel chart',
    example: 'Clicks → landing → ATC → purchase drop-off'
  },
  {
    id: 'retention',
    question: 'How does audience attention decay?',
    chartType: 'Retention curve',
    example: 'Video viewer % at each second'
  },
  {
    id: 'scatter',
    question: 'What is the spend vs return trade-off?',
    chartType: 'Scatter plot',
    example: 'Spend (X) vs iROAS (Y) by campaign'
  },
  {
    id: 'cohort',
    question: 'How do acquisition cohorts behave?',
    chartType: 'Cohort heatmap',
    example: 'Month-0 to month-6 repeat purchase rate'
  }
] as const

/**
 * Weekly MER time-series point.
 *
 * @typedef {Object} MerTimeSeriesPoint
 * @property {string} week - Week label.
 * @property {number} mer - Marketing efficiency ratio.
 * @property {number} metaRoas - Platform-reported ROAS.
 * @property {number} spend - Weekly ad spend in thousands.
 */
export interface MerTimeSeriesPoint {
  week: string
  mer: number
  metaRoas: number
  spend: number
}

/**
 * Channel CPA comparison row.
 *
 * @typedef {Object} ChannelCpaPoint
 * @property {string} channel - Channel name.
 * @property {number} cpa - Cost per acquisition.
 * @property {number} volume - Conversion volume for weighting context.
 */
export interface ChannelCpaPoint {
  channel: string
  cpa: number
  volume: number
}

/**
 * Funnel stage with absolute counts.
 *
 * @typedef {Object} FunnelStagePoint
 * @property {string} stage - Funnel stage label.
 * @property {number} count - Absolute user count.
 * @property {number} rate - Stage conversion rate from prior stage.
 */
export interface FunnelStagePoint {
  stage: string
  count: number
  rate: number
}

/**
 * Video retention second.
 *
 * @typedef {Object} RetentionPoint
 * @property {number} second - Playback second.
 * @property {number} viewers - Percentage of viewers still watching.
 */
export interface RetentionPoint {
  second: number
  viewers: number
}

/**
 * Campaign spend vs efficiency point.
 *
 * @typedef {Object} SpendEfficiencyPoint
 * @property {string} campaign - Campaign label.
 * @property {number} spend - Weekly spend in thousands.
 * @property {number} iroas - Incremental ROAS estimate.
 */
export interface SpendEfficiencyPoint {
  campaign: string
  spend: number
  iroas: number
}

/**
 * Creative variant with confidence interval.
 *
 * @typedef {Object} CreativeVariantPoint
 * @property {string} variant - Variant label.
 * @property {number} cpa - Cost per acquisition.
 * @property {number} ciLow - Lower 95% confidence bound.
 * @property {number} ciHigh - Upper 95% confidence bound.
 * @property {number} impressions - Sample size context.
 */
export interface CreativeVariantPoint {
  variant: string
  cpa: number
  ciLow: number
  ciHigh: number
  impressions: number
}

/**
 * Cohort retention cell.
 *
 * @typedef {Object} CohortCell
 * @property {string} cohort - Acquisition month label.
 * @property {number} month - Months since acquisition.
 * @property {number} repeatRate - Repeat purchase rate.
 */
export interface CohortCell {
  cohort: string
  month: number
  repeatRate: number
}

/**
 * Axis integrity demo data point.
 *
 * @typedef {Object} AxisIntegrityPoint
 * @property {string} week - Week label.
 * @property {number} revenue - Revenue in thousands.
 */
export interface AxisIntegrityPoint {
  week: string
  revenue: number
}

/**
 * Channel revenue mix used by pie vs bar comparison charts.
 */
export const CHANNEL_MIX_DATA = [
  { channel: 'Meta', share: 34 },
  { channel: 'Google', share: 22 },
  { channel: 'TikTok', share: 18 },
  { channel: 'YouTube', share: 12 },
  { channel: 'Email', share: 8 },
  { channel: 'Affiliate', share: 6 }
] as const

/**
 * Returns channel mix sorted by descending share.
 *
 * @returns {Array<{ channel: string; share: number }>} Sorted channel mix rows.
 */
export function generateChannelMixData(): Array<{ channel: string; share: number }> {
  return [...CHANNEL_MIX_DATA].sort((a, b) => b.share - a.share)
}

/**
 * Maps a cohort repeat rate to an accessible heatmap background color.
 *
 * @param {number} rate - Repeat rate between 0 and 1.
 * @returns {{ backgroundColor: string; textClassName: string }} Cell colors.
 */
export function cohortRateToColor(rate: number): { backgroundColor: string; textClassName: string } {
  const intensity = Math.min(1, Math.max(0, (rate - 0.08) / 0.4))
  const low = MARKETING_DATA_VIZ_PHASES.cohortLow
  const high = MARKETING_DATA_VIZ_PHASES.cohortHigh
  const parse = (hex: string): [number, number, number] => {
    const normalized = hex.replace('#', '')
    const bigint = Number.parseInt(normalized, 16)
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255]
  }
  const [r1, g1, b1] = parse(low)
  const [r2, g2, b2] = parse(high)
  const red = Math.round(r1 + (r2 - r1) * intensity)
  const green = Math.round(g1 + (g2 - g1) * intensity)
  const blue = Math.round(b1 + (b2 - b1) * intensity)
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255
  return {
    backgroundColor: `rgb(${red}, ${green}, ${blue})`,
    textClassName: luminance > 0.62 ? 'text-gray-900' : 'text-white'
  }
}

/**
 * Generates a 12-week MER trend dataset with a creative refresh annotation week.
 *
 * @returns {MerTimeSeriesPoint[]} Weekly efficiency series.
 */
export function generateMerTimeSeries(): MerTimeSeriesPoint[] {
  const weeks = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8', 'W9', 'W10', 'W11', 'W12']
  const rng = createDeterministicRandom(hashSeed('mer-time-series', weeks.length))
  return weeks.map((week, index) => {
    const baseMer = 2.4 + index * 0.04
    const noise = (rng() - 0.5) * 0.12
    const refreshBoost = index >= 6 ? 0.18 : 0
    const mer = Math.round((baseMer + noise + refreshBoost) * 100) / 100
    const metaRoas = Math.round((mer * 0.72 + (rng() - 0.5) * 0.08) * 100) / 100
    const spend = Math.round((42 + index * 1.8 + (rng() - 0.5) * 3) * 10) / 10
    return { week, mer, metaRoas, spend }
  })
}

/**
 * Generates channel CPA comparison data sorted by performance.
 *
 * @returns {ChannelCpaPoint[]} Channel CPA rows.
 */
export function generateChannelCpaData(): ChannelCpaPoint[] {
  return [
    { channel: 'Meta Prospecting', cpa: 38.2, volume: 1240 },
    { channel: 'Google Search Brand', cpa: 29.4, volume: 890 },
    { channel: 'TikTok Video', cpa: 44.7, volume: 620 },
    { channel: 'YouTube Demand Gen', cpa: 51.3, volume: 410 },
    { channel: 'Meta Retargeting', cpa: 22.1, volume: 760 },
    { channel: 'Affiliate', cpa: 35.8, volume: 280 }
  ].sort((a, b) => a.cpa - b.cpa)
}

/**
 * Generates a landing-to-purchase funnel with absolute counts.
 *
 * @returns {FunnelStagePoint[]} Funnel stages.
 */
export function generateMarketingFunnelData(): FunnelStagePoint[] {
  const stages = [
    { stage: 'Impressions', count: 1_250_000, rate: 1 },
    { stage: 'Clicks', count: 31_250, rate: 0.025 },
    { stage: 'Landing Views', count: 24_375, rate: 0.78 },
    { stage: 'Add to Cart', count: 2_925, rate: 0.12 },
    { stage: 'Purchases', count: 585, rate: 0.2 }
  ]
  return stages
}

/**
 * Returns funnel stages for the explorer visualization (skips impression volume).
 *
 * @returns {FunnelStagePoint[]} Click-through-purchase funnel stages.
 */
export function generateMarketingFunnelChartStages(): FunnelStagePoint[] {
  return generateMarketingFunnelData().filter(stage => stage.stage !== 'Impressions')
}

/**
 * CPA values for axis-integrity side-by-side comparison.
 *
 * @typedef {Object} CpaCampaignPoint
 * @property {string} campaign - Campaign label.
 * @property {number} cpa - Cost per acquisition in USD.
 */
export interface CpaCampaignPoint {
  campaign: string
  cpa: number
}

/**
 * Returns three CPA values in a narrow band — ideal for axis truncation demos.
 *
 * @returns {CpaCampaignPoint[]} Campaign CPA rows.
 */
export function generateCpaAxisComparisonData(): CpaCampaignPoint[] {
  return [
    { campaign: 'Campaign A', cpa: 48 },
    { campaign: 'Campaign B', cpa: 50 },
    { campaign: 'Campaign C', cpa: 52 }
  ]
}

/**
 * Generates a video retention curve with a mid-video cliff pattern.
 *
 * @returns {RetentionPoint[]} Retention by second.
 */
export function generateVideoRetentionData(): RetentionPoint[] {
  const points: RetentionPoint[] = []
  for (let second = 0; second <= 30; second += 1) {
    let viewers = 100
    if (second <= 3) {
      viewers = 100 - second * 11
    } else if (second <= 14) {
      viewers = 67 - (second - 3) * 1.8
    } else if (second <= 16) {
      viewers = 47 - (second - 14) * 9
    } else {
      viewers = 29 - (second - 16) * 1.4
    }
    points.push({ second, viewers: Math.max(8, Math.round(viewers * 10) / 10) })
  }
  return points
}

/**
 * Generates campaign spend vs incremental ROAS scatter data.
 *
 * @returns {SpendEfficiencyPoint[]} Campaign efficiency points.
 */
export function generateSpendEfficiencyData(): SpendEfficiencyPoint[] {
  return [
    { campaign: 'Brand Search', spend: 12, iroas: 4.8 },
    { campaign: 'Prospecting A', spend: 48, iroas: 1.9 },
    { campaign: 'Prospecting B', spend: 36, iroas: 2.4 },
    { campaign: 'Retargeting', spend: 22, iroas: 3.6 },
    { campaign: 'ASC Broad', spend: 64, iroas: 1.4 },
    { campaign: 'YouTube TOF', spend: 28, iroas: 1.1 },
    { campaign: 'TikTok Spark', spend: 18, iroas: 2.8 },
    { campaign: 'Influencer Whitelist', spend: 14, iroas: 3.1 }
  ]
}

/**
 * Generates creative test results with overlapping confidence intervals.
 *
 * @returns {CreativeVariantPoint[]} Variant CPA with CIs.
 */
export function generateCreativeTestData(): CreativeVariantPoint[] {
  return [
    { variant: 'Hook A — UGC', cpa: 34.2, ciLow: 31.8, ciHigh: 36.9, impressions: 84_200 },
    { variant: 'Hook B — Demo', cpa: 36.8, ciLow: 33.9, ciHigh: 39.7, impressions: 79_400 },
    { variant: 'Hook C — Problem', cpa: 38.5, ciLow: 35.1, ciHigh: 42.1, impressions: 62_100 },
    { variant: 'Control', cpa: 41.2, ciLow: 37.6, ciHigh: 45.3, impressions: 91_800 }
  ]
}

/**
 * Generates cohort repeat-rate heatmap cells.
 *
 * @returns {CohortCell[]} Cohort retention cells.
 */
export function generateCohortHeatmapData(): CohortCell[] {
  const cohorts = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']
  const cells: CohortCell[] = []
  cohorts.forEach((cohort, cohortIndex) => {
    for (let month = 0; month <= 5; month += 1) {
      const base = 0.42 - cohortIndex * 0.02
      const decay = month * 0.05
      const noise = ((cohortIndex + month) % 3) * 0.01
      const repeatRate = Math.max(0.08, Math.round((base - decay + noise) * 1000) / 1000)
      cells.push({ cohort, month, repeatRate })
    }
  })
  return cells
}

/**
 * Generates a flat revenue series for axis-integrity demonstration.
 *
 * @returns {AxisIntegrityPoint[]} Weekly revenue with minor variation.
 */
export function generateAxisIntegrityData(): AxisIntegrityPoint[] {
  const weeks = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8']
  const rng = createDeterministicRandom(hashSeed('axis-integrity-demo', weeks.length))
  return weeks.map((week, index) => ({
    week,
    revenue: Math.round((512 + index * 1.2 + (rng() - 0.5) * 4) * 10) / 10
  }))
}
